import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Generic polling hook.
 * @param {Function} fetchFn  - async function returning data
 * @param {number}   interval - ms between polls (default 10s)
 * @param {boolean}  enabled  - pause polling when false
 */
export function usePolling(fetchFn, interval = 10000, enabled = true) {
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastAt,  setLastAt]  = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const fetchFnRef = useRef(fetchFn);

  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  const fetch = useCallback(async () => {
    try {
      const result = await fetchFnRef.current();
      if (mountedRef.current) {
        setData(result);
        setError(null);
        setLastAt(new Date());
      }
    } catch (err) {
      if (mountedRef.current) setError(err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      setLoading(false);
      return () => { mountedRef.current = false; };
    }

    fetch();
    timerRef.current = setInterval(fetch, interval);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [fetch, interval, enabled]);

  return { data, error, loading, lastAt, refresh: fetch };
}

/**
 * SSE hook for live location streaming.
 * @param {string|null} url - SSE endpoint URL (null = disabled)
 */
export function useSSE(url) {
  const [event,  setEvent]  = useState(null);
  const [status, setStatus] = useState('disconnected');
  const esRef = useRef(null);

  useEffect(() => {
    if (!url) return;

    setStatus('connecting');
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen    = () => setStatus('connected');
    es.onerror   = () => setStatus('error');
    es.onmessage = (e) => {
      try { setEvent(JSON.parse(e.data)); }
      catch { setEvent(e.data); }
    };

    return () => { es.close(); setStatus('disconnected'); };
  }, [url]);

  return { event, status };
}
